import * as React from 'react'

import { Slot } from '@radix-ui/react-slot'
import { type VariantProps, cva } from 'class-variance-authority'
import { twMerge } from 'tailwind-merge'

import RotateRightIcon from '@/assets/icons/icon-rotate-right.svg'
import { classNames } from '@/utils/helpers.ts'

export const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 truncate',
  {
    variants: {
      variant: {
        default:
          'bg-primary-500 font-medium text-neutral-default shadow hover:bg-primary-600',
        destructive:
          'bg-danger-default font-medium text-neutral-default shadow-sm hover:bg-danger-default/90',
        outline:
          'border dark:border-border-default text-title bg-card hover:bg-primary-50/50 dark:hover:bg-secondary-600 hover:border-primary-200 hover:text-title',
        secondary:
          'bg-secondary-500 text-secondary-foreground shadow-sm hover:bg-secondary/80',
        ghost:
          'text-primary-500 hover:bg-transparent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        nav: 'border-none bg-transparent hover:bg-primary-500 shadow-none text-title flex justify-start',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  as?: React.ElementType
  isLoading?: boolean
  htmlFor?: string
  testId?: string
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      as,
      className,
      isLoading,
      children,
      variant,
      size,
      asChild = false,
      testId,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : (as ?? 'button')
    const loadingClass =
      isLoading || props.disabled ? 'opacity-50 pointer-events-none' : ''
    const combinedClassName = twMerge(
      buttonVariants({
        variant,
        size,
        className: classNames(loadingClass, className),
      }),
    )

    return (
      <Comp
        className={combinedClassName}
        ref={ref}
        {...props}
        data-testid={testId}
      >
        <>
          {isLoading && (
            <RotateRightIcon className="mr-2 h-5 w-5 animate-spin" />
          )}
          {children}
        </>
      </Comp>
    )
  },
)
Button.displayName = 'Button'
